/**
 * ========================
 * COMMUNICATIONS · EMAIL MENU PROVIDER
 * ========================
 *
 * Menu for the Email channel sub-app (`/admin/communications/email/*`).
 * `comms-email` is a `hidden` domain reached from the Communications hub.
 * Visibility is permission-driven (Communications.Email.*); Admins bypass.
 */

import {
  IconLayoutDashboard,
  IconPencil,
  IconUsersGroup,
  IconSend,
  IconTemplate,
  IconSettings,
  IconSpeakerphone,
} from '@tabler/icons-react';
import { DomainMenuProvider, MenuSection, DomainMenuConfig } from '../../layouts/menu/types';
import { createVisibilityCheck } from './BaseMenuProvider';
import { PERMISSIONS } from '../../config/permissions';

const P = PERMISSIONS.COMMUNICATIONS.EMAIL;
const BASE = '/admin/communications/email';

class CommsEmailMenuProvider implements DomainMenuProvider {
  domainId = 'comms-email';

  getMenuConfig(): DomainMenuConfig {
    const view = createVisibilityCheck({ permissions: [P.VIEW] });
    const send = createVisibilityCheck({ permissions: [P.SEND] });
    const manage = createVisibilityCheck({ permissions: [P.MANAGE] });
    const settings = createVisibilityCheck({ permissions: [P.SETTINGS] });
    const campaigns = createVisibilityCheck({ permissions: [PERMISSIONS.COMMUNICATIONS.CAMPAIGNS.VIEW] });

    const sections: MenuSection[] = [
      {
        id: 'comms-email-overview',
        label: 'Overview',
        visible: view,
        items: [
          { id: 'comms-email-dashboard', label: 'Dashboard', path: BASE, icon: <IconLayoutDashboard size={16} />, visible: view },
        ],
      },
      {
        id: 'comms-email-send',
        label: 'Send',
        visible: send,
        items: [
          { id: 'comms-email-compose', label: 'Compose', path: `${BASE}/compose`, icon: <IconPencil size={16} />, visible: send },
          { id: 'comms-email-bulk', label: 'Bulk Send', path: `${BASE}/bulk`, icon: <IconUsersGroup size={16} />, visible: send },
        ],
      },
      {
        id: 'comms-email-messages',
        label: 'Messages',
        visible: view,
        items: [
          { id: 'comms-email-outbox', label: 'Outbox', path: `${BASE}/outbox`, icon: <IconSend size={16} />, visible: view },
          { id: 'comms-email-campaigns', label: 'Campaigns', path: `${BASE}/campaigns`, icon: <IconSpeakerphone size={16} />, visible: campaigns },
        ],
      },
      {
        id: 'comms-email-library',
        label: 'Library',
        visible: manage,
        items: [
          { id: 'comms-email-templates', label: 'Templates', path: `${BASE}/templates`, icon: <IconTemplate size={16} />, visible: manage },
        ],
      },
      {
        id: 'comms-email-config',
        label: 'Configuration',
        visible: settings,
        items: [
          { id: 'comms-email-settings', label: 'Settings', path: `${BASE}/settings`, icon: <IconSettings size={16} />, visible: settings },
        ],
      },
    ];

    return {
      domainId: this.domainId,
      sections,
      metadata: { version: '1.0.0', lastUpdated: Date.now(), description: 'Email channel — admin console' },
    };
  }
}

export const commsEmailMenuProvider = new CommsEmailMenuProvider();
